import React from 'react';
import TeachEnglishPageLayout from '../components/TeachEnglishPageLayout';

const RecycleVocabulary = () => {
  const title = 'Recycle Vocabulary';
  const image = '/Images/vocabulary.webp';
  const intro = 'Students forget 70% of new words within 24 hours without deliberate recycling. This guide gives you a systematic, low-prep framework for spaced repetition, contextual re-encounters, and productive retrieval that moves vocabulary into long-term memory.';

  const seoContent = [
    'The forgetting curve (Ebbinghaus) is steep: without review, retention drops to 30% after a day, 10% after a week. The solution isn\'t more new words—it\'s structured re-exposure. Aim for 7–12 encounters per lexical item across varied contexts (reading, listening, speaking, writing) spaced over 1–2 weeks.',
    'Build a recycling routine into every lesson: 5-minute warm-up revisiting last lesson\'s key words (odd-one-out, taboo, collocation dominoes), a mid-lesson \"vocab cameo\" where target items reappear in a new task, and a 2-minute exit ticket (write three words, define one, use one in a sentence).',
    'Use the \"expanding rehearsal\" schedule: review new words 10 minutes after introduction, end of lesson, 24 hours later, 3 days, 1 week, 2 weeks. A simple spreadsheet with columns for Word, Date Introduced, Review Dates, and Mastery (✓/✗) keeps you honest.',
    'Shift from receptive to productive recycling. Early encounters: matching, multiple choice, gapped sentences. Later: open cloze, sentence transformation, spoken retell using target words, written summary with a required-word list. Productive use cements memory.',
    'Teach learning strategies alongside words: keyword method, semantic mapping, personalization (\"write a true sentence about yourself\"), and digital tools (Anki, Quizlet) with shared class decks. Students who know *how* to review become autonomous vocabulary learners.',
  ];

  const levels = [
    {
      level: 'techniques',
      label: 'Recycling Techniques',
      tagline: 'Low-prep, high-impact activities',
      cards: [
        {
          title: 'Vocab Warm-Up Menu',
          image: '/Images/grammar1.webp',
          desc: '10 zero-prep 5-minute starters: Taboo, Pictionary, Collocation Race, Word Ladder, etc.',
          links: [{ text: 'Download Menu', to: '/resources/vocab-warmups.pdf' }],
        },
        {
          title: 'Spaced Review Tracker',
          image: '/Images/2people1.webp',
          desc: 'Google Sheet template with conditional formatting for expanding rehearsal dates.',
          links: [{ text: 'Copy Template', to: '/tools/vocab-tracker' }],
        },
        {
          title: 'Productive Retrieval Tasks',
          image: '/Images/vocabulary.webp',
          desc: 'From controlled (gap-fill) to free (story chain, debate prep) with CEFR-level examples.',
          links: [{ text: 'View Tasks', to: '/vocabulary-exercises/retrieval' }],
        },
      ],
    },
    {
      level: 'digital',
      label: 'Digital Tools & Decks',
      tagline: 'Shared class sets & automation',
      cards: [
        {
          title: 'Anki Class Deck Setup',
          image: '/Images/online-teacher.webp',
          desc: 'Step-by-step: create deck, add cloze cards, share via AnkiWeb, monitor student stats.',
          links: [{ text: 'Watch Guide', to: '/videos/anki-setup' }],
        },
        {
          title: 'Quizlet Live for Review',
          image: '/Images/tutor2-1280x470.webp',
          desc: 'Team game mode walkthrough: create sets, assign teams, run session, export results.',
          links: [{ text: 'Watch Guide', to: '/videos/quizlet-live' }],
        },
        {
          title: 'Custom Wordlist Generator',
          image: '/Images/3people1a.webp',
          desc: 'Paste a text → get CEFR-leveled wordlist with definitions, collocations, and audio links.',
          links: [{ text: 'Try Tool', to: '/tools/wordlist-generator' }],
        },
      ],
    },
  ];

  return (
    <TeachEnglishPageLayout
      title={title}
      image={image}
      intro={intro}
      seoContent={seoContent}
      levels={levels}
      seoTitle="Vocabulary Recycling Strategies: Spaced Repetition, Retrieval Practice & Digital Tools"
      seoDescription="Systematic framework for recycling English vocabulary. 7–12 encounters per word, expanding rehearsal schedule, productive retrieval tasks, and free Anki/Quizlet setup guides for ESL teachers."
    />
  );
};

export default RecycleVocabulary;